import {
  AppShell as MantineAppShell,
  Avatar,
  Burger,
  Group,
  Menu,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconLogout } from '@tabler/icons-react';
import { useCallback, useContext, type ReactNode } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAdminAgentNavigationTool } from '../agent/useAdminAgentNavigationTool';
import { useAuth } from '../auth/useAuth';
import { adminConfig } from '../config/env';
import { DemoModeContext } from '../demo/context';
import { useUiStore } from '../store/uiStore';
import { SideMenu } from './SideMenu';

/** Caparazón del panel: header + navbar lateral + contenido (ruta hija). */
export function AppShell(): ReactNode {
  const demo = useContext(DemoModeContext);
  const { status, isAdmin, user, signOut } = useAuth();
  const navigate = useNavigate();
  const navbarOpened = useUiStore((s) => s.navbarOpened);
  const toggleNavbar = useUiStore((s) => s.toggleNavbar);
  const setNavbar = useUiStore((s) => s.setNavbar);
  const closeNavbar = useCallback(() => setNavbar(false), [setNavbar]);

  useAdminAgentNavigationTool({
    enabled:
      adminConfig.webMcpEnabled &&
      status === 'signed-in' &&
      isAdmin,
    navigate,
    onNavigate: closeNavbar,
  });

  const initial = (user?.displayName ?? user?.email ?? '?').charAt(0).toUpperCase();

  return (
    <MantineAppShell
      header={{ height: demo ? 100 : 56 }}
      navbar={{ width: 260, breakpoint: 'sm', collapsed: { mobile: !navbarOpened } }}
      padding="md"
    >
      <MantineAppShell.Header>
        {demo && <div style={{ height: 44, display: 'flex', alignItems: 'center', background: '#fff0c2', color: '#4a3300', padding: '0 16px', fontSize: 13 }}>DEMOSTRACIÓN LOCAL · Datos ficticios · Sin envíos ni validez fiscal</div>}
        <Group h={56} px="md" justify="space-between">
          <Group gap="sm">
            <Burger
              opened={navbarOpened}
              onClick={toggleNavbar}
              hiddenFrom="sm"
              size="sm"
              aria-label={navbarOpened ? 'Cerrar menú principal' : 'Abrir menú principal'}
              aria-controls="admin-primary-navigation"
              aria-expanded={navbarOpened}
              data-agent-id="admin.shell.menu-toggle"
            />
            <Title order={4}>Admin Ibercar</Title>
          </Group>
          <Menu position="bottom-end" withArrow>
            <Menu.Target>
              <UnstyledButton
                aria-label="Abrir menú de cuenta"
                data-agent-id="admin.shell.account-menu"
              >
                <Avatar radius="xl" color="ibercar">
                  {initial}
                </Avatar>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>
                <Text size="xs" truncate>
                  {user?.email ?? 'Sesión'}
                </Text>
              </Menu.Label>
              <Menu.Item leftSection={<IconLogout size={16} />} onClick={() => void signOut()}>
                Cerrar sesión
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </MantineAppShell.Header>

      <MantineAppShell.Navbar
        id="admin-primary-navigation"
        p="sm"
        aria-label="Navegación principal"
      >
        <SideMenu />
      </MantineAppShell.Navbar>

      <MantineAppShell.Main data-agent-id="admin.page.content">
        <Outlet />
      </MantineAppShell.Main>
    </MantineAppShell>
  );
}
